// Pure helpers behind Predict's dashboard (STRY0039090). The page reads
// GET /predict/watchlist, /predict/counts, /predict/scope and /predict/outcomes;
// list bugs are {vendor, bugId, title, url, filed, score, promoted, reason, cve,
// weaknessType, scorerVersion, source, cwe, target, targetName}. Dates and
// scores use the watch list's helpers.

import { bugSource } from './predictSources';

// The watch list's own page. It reads `project` (an archive vendor id, once per
// vendor), `q` and `min` from its query string.
export const WATCH_PATH = '/predict/watch';

// The watch list filtered to these archive vendor ids: /predict/watch?project=a&project=b.
export const watchListPath = (vendors = []) => {
  const params = new URLSearchParams();
  [...new Set(vendors)].forEach(vendor => params.append('project', vendor));
  const query = params.toString();
  return query ? `${WATCH_PATH}?${query}` : WATCH_PATH;
};

const isScore = score => typeof score === 'number' && Number.isFinite(score);

const isText = value => typeof value === 'string' && value.trim() !== '';

// One row per project on the list, most bugs first, then by name:
// [{target, name, vendors, count, topScore, newestFiled, source, inventoryVendorId, inventoryProducts}]. `vendors`
// are the archive vendor ids its bugs carry, for the watch list's filter;
// `source` is the tracker most of its bugs were filed on ('github', 'jira',
// ...; `bugSource`: the bug's own source, else its link's host), or null when
// none is known.
export const projectRows = bugs => {
  const byTarget = new Map();
  bugs.forEach(bug => {
    const key = bug.target || bug.vendor;
    let row = byTarget.get(key);
    if (!row) {
      row = {
        target: key,
        name: bug.targetName || key,
        vendors: [],
        count: 0,
        topScore: null,
        newestFiled: null,
        // Why the project is in scope (STRY0039118): the same for every bug of one target.
        inventoryVendorId: bug.inventoryVendorId || null,
        inventoryProducts: Array.isArray(bug.inventoryProducts) ? bug.inventoryProducts : []
      };
      row.sources = new Map();
      byTarget.set(key, row);
    }
    row.count += 1;
    if (!row.vendors.includes(bug.vendor)) row.vendors.push(bug.vendor);
    if (isScore(bug.score) && (row.topScore === null || bug.score > row.topScore)) row.topScore = bug.score;
    if (isText(bug.filed) && (row.newestFiled === null || bug.filed > row.newestFiled)) row.newestFiled = bug.filed;
    const source = bugSource(bug);
    if (source) row.sources.set(source, (row.sources.get(source) || 0) + 1);
  });
  return [...byTarget.values()]
    .map(({ sources, ...row }) => ({
      ...row,
      source: [...sources.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0]?.[0] || null
    }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
};

// The two conversion classes a customer sees, in Eric's words (STRY0038978).
// Any other class is never shown.
export const CLASS_LABEL = { predicted: 'Prediction', 'retro-matched': 'Detection' };

const hasClass = cls => typeof cls === 'string' && Object.prototype.hasOwnProperty.call(CLASS_LABEL, cls);

// The outcomes the dashboard can show: a bug, a CVE and a known class each.
export const shownOutcomes = outcomes =>
  Array.isArray(outcomes)
    ? outcomes.filter(o => o && isText(o.vendor) && isText(o.bugId) && isText(o.cve) && hasClass(o.class))
    : [];

// Caught bugs, each class on its own. The counting rule never adds Prediction
// and Detection together, so there is no total.
export const caughtCounts = outcomes => ({
  predicted: outcomes.filter(o => o.class === 'predicted').length,
  'retro-matched': outcomes.filter(o => o.class === 'retro-matched').length
});

// The newest first public statements first; an outcome with no date goes last.
// Ties keep the API's order.
export const recentOutcomes = (outcomes, limit = 5) =>
  outcomes
    .map((outcome, index) => ({ outcome, index }))
    .sort((a, b) => {
      const da = isText(a.outcome.firstPublicAt) ? a.outcome.firstPublicAt : null;
      const db = isText(b.outcome.firstPublicAt) ? b.outcome.firstPublicAt : null;
      if (da === null || db === null) return (da === null) - (db === null) || a.index - b.index;
      return db.localeCompare(da) || a.index - b.index;
    })
    .slice(0, limit)
    .map(({ outcome }) => outcome);
